import { useState, type ReactNode } from 'react';
import { BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

export type GuideStep = {
  title: string;
  body?: string;
  bullets?: string[];
  note?: string;
  code?: string;
};

export type SetupGuide = {
  title: string;
  intro?: string;
  pathHint?: string;
  steps: GuideStep[];
  tips?: string[];
  warning?: string;
  defaultOpen?: boolean;
};

/**
 * Collapsible setup guidelines — same pattern as Shopify Integration.
 */
export default function SetupGuidePanel({
  guide,
  accent = 'teal',
  footer,
}: {
  guide: SetupGuide;
  accent?: 'teal' | 'green' | 'blue' | 'amber' | 'sky';
  footer?: ReactNode;
}) {
  const [open, setOpen] = useState(Boolean(guide.defaultOpen));

  const accents: Record<string, { border: string; bg: string; title: string; chevron: string; path: string }> = {
    teal: {
      border: 'border-teal-200',
      bg: 'from-teal-50/80 to-white',
      title: 'text-teal-900',
      chevron: 'text-teal-700',
      path: 'border-teal-200 text-teal-900',
    },
    green: {
      border: 'border-green-200',
      bg: 'from-green-50/80 to-white',
      title: 'text-green-900',
      chevron: 'text-green-700',
      path: 'border-green-200 text-green-900',
    },
    blue: {
      border: 'border-blue-200',
      bg: 'from-blue-50/80 to-white',
      title: 'text-blue-900',
      chevron: 'text-blue-700',
      path: 'border-blue-200 text-blue-900',
    },
    amber: {
      border: 'border-amber-200',
      bg: 'from-amber-50/80 to-white',
      title: 'text-amber-950',
      chevron: 'text-amber-800',
      path: 'border-amber-200 text-amber-950',
    },
    sky: {
      border: 'border-sky-200',
      bg: 'from-sky-50/80 to-white',
      title: 'text-sky-900',
      chevron: 'text-sky-700',
      path: 'border-sky-200 text-sky-900',
    },
  };
  const a = accents[accent] || accents.teal;

  return (
    <div className={`rounded-xl border ${a.border} bg-gradient-to-br ${a.bg} overflow-hidden min-w-0`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
      >
        <span className={`flex items-center gap-2 text-sm font-semibold ${a.title} min-w-0`}>
          <BookOpen className="w-4 h-4 shrink-0" />
          <span className="truncate">{guide.title}</span>
        </span>
        {open ? (
          <ChevronUp className={`w-4 h-4 ${a.chevron} shrink-0`} />
        ) : (
          <ChevronDown className={`w-4 h-4 ${a.chevron} shrink-0`} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
          {guide.intro && <p className="text-gray-600 break-words">{guide.intro}</p>}

          {guide.pathHint && (
            <div className={`rounded-lg border bg-white px-3 py-2.5 text-xs ${a.path} min-w-0`}>
              <p className="font-semibold mb-1">Where to look</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                {guide.pathHint}
              </p>
            </div>
          )}

          <ol className="space-y-3 list-decimal list-inside">
            {guide.steps.map((step) => (
              <li key={step.title}>
                <span className="font-medium text-gray-900">{step.title}</span>
                {step.body && <p className="mt-1 ml-5 text-gray-600 break-words">{step.body}</p>}
                {step.bullets && step.bullets.length > 0 && (
                  <ul className="mt-1.5 ml-5 list-disc list-inside text-gray-600 space-y-1">
                    {step.bullets.map((b) => (
                      <li key={b} className="break-words">
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {step.code && (
                  <code className="mt-2 ml-5 block text-[11px] bg-white border border-gray-200 rounded px-2 py-1.5 break-all whitespace-pre-wrap">
                    {step.code}
                  </code>
                )}
                {step.note && (
                  <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950">
                    {step.note}
                  </div>
                )}
              </li>
            ))}
          </ol>

          {guide.warning && (
            <div className="rounded-lg bg-amber-50/80 border border-amber-100 p-3 text-xs text-amber-950">
              {guide.warning}
            </div>
          )}

          {guide.tips && guide.tips.length > 0 && (
            <div className="rounded-lg bg-white/80 border border-gray-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              {guide.tips.map((tip) => (
                <p key={tip}>• {tip}</p>
              ))}
            </div>
          )}

          {footer}
        </div>
      )}
    </div>
  );
}
